import { canAccess, hasRole, type Area } from './access.js';
import type { TenantRole } from '@esa/shared';

/**
 * Help content, as data.
 *
 * Two rules govern this file, and both exist because help that lies is worse
 * than no help:
 *
 *   1. Every topic declares the `area` it belongs to, and the same
 *      `canAccess` the navigation uses decides whether it is shown. Nobody is
 *      ever told how to do something the API will refuse — a requester
 *      reading "assign the ticket to yourself" learns only that the tool is
 *      confusing.
 *
 *   2. A topic describes what the system ACTUALLY does today. Where something
 *      is deliberately absent, the topic says so plainly rather than being
 *      omitted, because "I couldn't find it" is a worse outcome than "it isn't
 *      built yet".
 *
 * `minRole` narrows further than the area where a single page mixes
 * capabilities — moving a ticket between queues is on the queue page, but it
 * is a queue_lead capability, so an agent must not be told to do it.
 */

export interface HelpTopic {
  id: string;
  title: string;
  /** One line, shown in the list. */
  summary: string;
  /** The body, as short paragraphs. Markdown is deliberately not supported. */
  body: string[];
  area: Area;
  /** Narrower than the area when one page mixes capabilities. */
  minRole?: TenantRole;
  /** Free-text search terms beyond the title and summary. */
  keywords?: string[];
  /** Where the topic is about, for the "open it" link. */
  path?: string;
  shortcuts?: { keys: string; does: string }[];
}

export interface HelpSection {
  id: string;
  title: string;
  blurb: string;
  area: Area;
  minRole?: TenantRole;
  topics: HelpTopic[];
}

const SECTIONS: HelpSection[] = [
  // -------------------------------------------------------------------------
  {
    id: 'basics',
    title: 'Getting around',
    blurb: 'The things that are true on every page.',
    area: 'personal',
    topics: [
      {
        id: 'command-palette',
        title: 'The command palette',
        summary: 'Press ⌘K (Ctrl+K) to jump anywhere or find a ticket by number or text.',
        area: 'personal',
        keywords: ['search', 'shortcut', 'cmdk', 'jump', 'navigate'],
        shortcuts: [
          { keys: '⌘K / Ctrl+K', does: 'Open the palette' },
          { keys: '#1234', does: 'Jump straight to that ticket' },
          { keys: 'Esc', does: 'Close it' },
        ],
        body: [
          'The palette searches ticket subjects and message text, and it also lists every page you can open. It will not offer a page your role cannot reach.',
          'Typing a number with a # in front goes straight to that ticket rather than searching for it.',
        ],
      },
      {
        id: 'groups',
        title: 'Switching between groups',
        summary: 'The group picker in the top bar changes which team you are working in.',
        area: 'personal',
        keywords: ['tenant', 'team', 'switch', 'esatech', 'publications', 'safety'],
        body: [
          'Each group — ESAtech, Publications, Health & Safety, Human Resources and the admin teams — is a separate tenant with its own queues, request types, fields and SLA targets. Nothing crosses between them.',
          'You only see the groups you have a role in, and your role can differ per group: you might be an agent in one and only a requester in another. The page you are on changes to match — if you switch to a group where you are only a requester, you land on your own requests rather than on a queue you cannot open.',
        ],
      },
      {
        id: 'raise',
        title: 'Raising a request',
        summary: 'New request, or just email the desk — both arrive in the same place.',
        area: 'personal',
        path: '/new',
        keywords: ['new', 'submit', 'email', 'ticket', 'ask'],
        body: [
          'The form asks only for what the group has configured. Fields differ per group and per request type, so choosing the type first usually shortens the form.',
          'Emailing the desk works identically and is how most requests actually arrive. The reply threading is by message headers, so answering the notification adds to the same ticket rather than opening a second one.',
          'Either way, the request is read against what each of the group’s queues is for and lands in the one it is about. A sensitive matter — a harassment complaint, say — goes to a private queue that only the people who handle it can see. You can always see your own request.',
        ],
      },
      {
        id: 'my-requests',
        title: 'Following your own requests',
        summary: 'My requests shows everything you raised, open or closed.',
        area: 'personal',
        path: '/my',
        keywords: ['mine', 'status', 'track', 'progress'],
        body: [
          'You see your own requests in every group you belong to. Replying on a ticket reopens it if it had been resolved and the reopen window has not passed.',
          'Whether you are shown the SLA target is a per-group setting, so it may be visible in one group and not another.',
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'queue',
    title: 'Working the queue',
    blurb: 'Finding, triaging and answering work.',
    area: 'queue',
    topics: [
      {
        id: 'views',
        title: 'List and board',
        summary: 'The same tickets, two shapes. Board is for triage, list is for volume.',
        area: 'queue',
        path: '/queue',
        keywords: ['board', 'kanban', 'list', 'columns', 'drag'],
        shortcuts: [
          // These had ↵ and O the wrong way round — the opposite of both the
          // code and the row's own context menu. Somebody following the help
          // pressed O expecting a preview and left the queue.
          { keys: 'J / K', does: 'Move down and up the list' },
          { keys: '↵', does: 'Peek at the highlighted ticket without leaving the queue' },
          { keys: 'O', does: 'Open the full ticket' },
          { keys: 'Esc', does: 'Close the peek' },
        ],
        body: [
          'Board groups by state and you can drag a card between columns. Dropping onto a waiting column asks what is being waited on, because that answer is what makes the blocked-time report possible at all.',
          'The list sorts and filters on every column, and the filter chips above it — Mine, Unassigned, At risk, Overdue, Needs routing, Blocked — are the views worth having as one click.',
          'When somebody else gives you a ticket you get an email about it, and the bell at the top lists it until you open it. To be told on your computer even with Tributary closed, open the bell and choose Turn on under it; that is per browser, so do it on each computer you use. Once it is on you can also ask to hear about every new ticket on the desk, and Send a test says whether a notification reached this browser — if it did and nothing appeared, Windows is hiding it (Settings, System, Notifications).',
          'Views (the bookmark button) keeps the tab, filters, sort and layout you have set up, to come back to in one click. Mark one "Start here" and the queue opens on it. The palette button gives rows a color — by owner, requester, queue, type, priority or tag — and the first rule a row matches wins. Both are yours alone and kept per desk.',
          'A small plane on the owner’s face means their Outlook automatic replies say they are out, and hovering says until when. While anybody holding open work is away, an "Owner away" chip lists those tickets so they can be handed to somebody who is in.',
        ],
      },
      {
        id: 'sla-ring',
        title: 'Reading the SLA ring',
        summary: 'The small circle is how much of the target this ticket has used.',
        area: 'queue',
        keywords: ['sla', 'target', 'breach', 'due', 'clock', 'paused'],
        body: [
          'The ring fills as the target is consumed and turns amber near the line and red past it. A hollow, paused ring means the clock is stopped because the ticket is waiting on somebody else.',
          'Targets are consumed in BUSINESS hours on the group’s own calendar. A ticket that arrives at 6pm on Friday has used none of its target by Monday morning.',
          'That pause is the point. Time spent waiting on a requester or a vendor is not time the desk was slow, and reporting it separately is what stops a genuinely blocked ticket from looking like a failure.',
        ],
      },
      {
        id: 'answering',
        title: 'Replying and making notes',
        summary: 'A reply goes to the requester; a note never leaves the desk.',
        area: 'queue',
        keywords: ['reply', 'note', 'internal', 'respond', 'email'],
        body: [
          'Replies are emailed to the requester and threaded onto the original message. Notes are internal and are never sent — they are the right place for "I think this is actually a licence problem".',
          'Anyone else who was on the request’s email is copied on replies too. The Cc line under Details shows who; take someone off with the × beside their name, or copy someone new.',
          'The reply you send is the stripped body: quoted history and signature blocks are cut before display, and the original is kept underneath if you need it.',
          'AI Proof, under the reply box, offers a tidied, professional version of what you wrote, next to yours. It never changes facts, names, numbers, links or steps, and never adds a promise. Nothing is sent until you choose to use it.',
          'The reply box has formatting: bold, italic, strikethrough, code, bulleted and numbered lists, and links (Ctrl+B and Ctrl+I work too; Ctrl+Enter sends). The arrow beside Send reply offers Send and wait for the requester, which sends and moves the ticket to waiting on the requester in one step. The requester gets it formatted in the email. Pasting from Outlook or a web page keeps that much formatting and drops the rest.',
          'Suggest a reply (when the group has Suggested replies switched on) drafts the next reply the way you usually write: it is shown a few replies you sent before, the same kind of request first, plus this ticket and the knowledgebase. Facts come only from this ticket and the articles. It learns from what you actually send, so editing a suggestion teaches the next one. It is never offered on a confidential ticket, and never sends anything itself.',
        ],
      },
      {
        id: 'attachments',
        title: 'Attaching files to a reply',
        summary: 'The Attach button, dragging a file onto the reply box, or pasting a screenshot.',
        area: 'queue',
        keywords: ['attach', 'attachment', 'file', 'upload', 'screenshot', 'paste', 'drag', 'image', 'picture'],
        body: [
          'Three ways, all in the reply box: press Attach under it, drag a file from your desktop onto it ("Drop to attach" appears), or paste a screenshot with Ctrl+V. Each file is listed with its size before you send, and can be taken off again.',
          'The files go out with the reply, in the same email. They are uploaded the moment the reply is posted, so the email waits a few seconds for them.',
          'If a file does not make it, the reason is shown under it in words — larger than the size limit, or file storage not set up for this deployment. The reply itself has already gone by then, so send the file again in a follow-up.',
          'Pictures show in the conversation next to the message they came with. Clicking any file downloads it; nothing is opened inside the page.',
        ],
      },
      {
        id: 'assign',
        title: 'Taking a ticket',
        summary: 'Assign it to yourself from the row menu, the board card or the ticket itself.',
        area: 'queue',
        keywords: ['assign', 'owner', 'mine', 'claim'],
        body: [
          'Right-click any row or card for the context menu. Assigning to yourself is one click; taking a ticket also clears the "needs routing" flag, because a person has now looked at it.',
          'A queue can also hand new work out by itself — in turn, or to whoever has the fewest open tickets — if its administrator has switched that on. A ticket waiting for a person to route it, or one the assistant already answered, is never handed out. Nor is anything handed to somebody whose automatic replies say they are out: they are skipped until they are back, and if everybody in the rotation is away the ticket waits in the queue.',
        ],
      },
      {
        id: 'reassign',
        title: 'Assigning to somebody else, and moving queues',
        summary: 'Both need queue lead.',
        area: 'queue',
        minRole: 'queue_lead',
        keywords: ['reassign', 'move', 'queue', 'transfer', 'route'],
        body: [
          'Assigning work to another person and moving a ticket to a different queue are queue lead capabilities. An agent can take a ticket but not hand one out.',
          'Moving a ticket to another GROUP is a transfer rather than a move: it needs a reason, and it leaves a link on both sides so the history is not lost.',
          'To move one ticket, use the Queue dropdown under Details on the ticket, or right-click its row and choose Move to queue. For several, tick them and use Move to queue… in the bar that appears.',
          'Moving into a restricted queue (marked "(restricted)") asks you to confirm first, wherever you do it from: if you are not a member, you lose sight of the ticket the moment it lands.',
        ],
      },
      {
        id: 'merge',
        title: 'Merging a duplicate',
        summary: 'More → Merge into another ticket. One is cancelled and linked to the one that carries on.',
        area: 'queue',
        minRole: 'queue_lead',
        keywords: ['merge', 'duplicate', 'twice', 'combine', 'same request'],
        body: [
          'On the duplicate, press More at the top of the ticket and choose Merge into another ticket… (or right-click its row and choose the same). Give the number of the ticket to keep and a reason.',
          'The duplicate is cancelled and linked to the one you kept, and a note on the kept ticket says what was folded into it. The reason stays with the desk.',
          'Its requester is emailed, as a reply from you, that the request carries on as the other number and they need do nothing. Untick the "Email the requester" box in the dialog if they should not be told — for instance when they sent the same email twice a minute apart.',
          'A merge cannot be undone from the screen. If two tickets are only connected, link them instead (see Linking tickets).',
        ],
      },
      {
        id: 'links',
        title: 'Linking tickets',
        summary: 'Related, parent and child, or duplicate — from the Related tickets panel or the row menu.',
        area: 'queue',
        keywords: ['link', 'related', 'relate', 'parent', 'child', 'tie', 'connect', 'duplicate'],
        body: [
          'On a ticket, the Related tickets panel at the top of the right-hand side has + Link. Find the other ticket by number or by words in its subject, choose how they are related, and press Link. From the queue, right-click a row and choose Relate to… for the same dialog.',
          'Parent of is one request that became several pieces of work (onboarding into an account and a laptop). Related to is worth reading together, with neither part of the other. Duplicate of marks the same request arriving twice without cancelling either — merge instead if one should be closed.',
          'Both tickets show the link. In the queue, the row carries a badge naming the other ticket: Parent of #…, Child of #…, or Related to #…. Remove a link with the × beside it, from either ticket.',
        ],
      },
      {
        id: "context-panels",
        title: "The panels beside a ticket",
        summary: "What this person asked lately, what fixed the same problem before, and what they asked for.",
        area: 'queue',
        keywords: ["history", "solved", "similar", "requester", "asks", "checklist", "panel"],
        body: [
          "\"What this person asked lately\" lists the requester’s other tickets, flags when they keep asking about the same thing, and names the ones still open.",
          "\"Solved before\" lists resolved tickets with the SAME problem and what fixed them — a model checks each match, so a ticket that only shares a word like \"laptop\" or the person’s name is left out. When the desk has never solved it, the panel is empty, which is the honest answer. Where the desk has solved something more than once and no article covers it, it offers to draft one.",
          "\"What they asked for\" appears when an email asked for several things. Each one shows whether the assistant already answered it, and from which article, or whether it is still to do. Tick an item off when it is done.",
        ],
      },
      {
        id: "known-solutions",
        title: "Known solutions and the match figure",
        summary: "Articles that may answer the ticket, with how closely they match — and nothing when nothing does.",
        area: 'queue',
        keywords: ["known solutions", "suggestion", "article", "match", "percent", "knowledgebase", "kb", "confidence"],
        body: [
          "When the knowledgebase has an article that may answer a ticket, a line above the conversation names it and a Known solutions tab appears beside Conversation. When nothing is close enough, neither appears — there is nothing to look at, which is the honest answer.",
          "Each suggestion shows a match figure, such as \"82% match\". At 75% or above it is offered as a Known solution. Below that it says so in words — \"closest, not confirmed\" — and the line above the conversation reads \"Closest in the knowledgebase\" instead: usually the nearest article about a different task.",
          "Read article opens the text; Use in reply puts a usable answer and a link in the reply box for you to edit before sending.",
        ],
      },
      {
        id: "actions",
        title: "Proposed actions (Egnyte)",
        summary: "A change to another system is proposed, approved by its owner, then run by you.",
        area: 'queue',
        keywords: ["action", "egnyte", "folder", "access", "approve", "owner", "propose"],
        body: [
          "On a desk with Actions switched on, a request like \"give me access to this folder\" becomes a proposed action on the Actions tab: exactly what will be done, to which folder, for whom, at what level, and why. Folder paths written the Windows way (Z:\\Shared\\…) are understood.",
          "Nothing runs until two things happen: an owner of that system approves it, and you say yes. The proposal names who can approve it, or says plainly that nobody owns the system yet. You can withdraw it at any point before it runs.",
          "If the system cannot be reached, the action is retried and then marked failed with the reason — with a warning to check before trying again, because it may or may not have run.",
        ],
      },
      {
        id: "confidential",
        title: "Confidential tickets",
        summary: "Tickets in a restricted queue are visible only to its members and the requester.",
        area: 'queue',
        keywords: ["confidential", "private", "restricted", "hr", "harassment", "sensitive", "members"],
        body: [
          "A restricted queue’s tickets are hidden from everyone who is not a member — the queue, search, reports, the history panels, all of it. Administrators are not members unless they add themselves, and the members can see when they do. The requester always sees their own ticket.",
          "Members see a confidential notice on the ticket and a \"Who has opened this\" log. Confidential tickets are never answered from the knowledgebase, never mined for articles, and never sent to webhooks. Reports say how many they leave out, and nothing else about them.",
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'intake',
    title: 'Intake and classification',
    blurb: 'How mail becomes a ticket, and what to do when it goes wrong.',
    area: 'intake',
    topics: [
      {
        id: 'ladder',
        title: 'How a message gets classified',
        summary: 'Seven rungs, cheapest first. Most mail never reaches a model.',
        area: 'intake',
        path: '/intake',
        keywords: ['classify', 'ladder', 'routing', 'rules', 'ai', 'model'],
        body: [
          'Incoming mail is stripped, then checked against machine-mail patterns, then known templates, then pattern rules, then the systems it names, then the working context it mentions. Only what survives all of that is routed by anything cleverer.',
          'The first three rungs never call a model at all. That is deliberate: a notification from a monitoring system should cost nothing to recognise, and roughly a fifth of what arrives is exactly that.',
          'The intake page replays a message through the ladder and shows which rung claimed it and why. It is the fastest way to answer "why did this land in the wrong queue".',
        ],
      },
      {
        id: "subject-routing",
        title: "Which queue a request lands in",
        summary: "Read against what every queue is for — the same for email, the portal and integrations.",
        area: 'intake',
        keywords: ["route", "routing", "queue", "description", "subject", "portal", "threshold"],
        body: [
          "Every new request is read by a model against each queue’s description (\"What this queue is for\") and sent to the one it is about, by meaning rather than shared words. It is the same whether it arrived by email, through the portal or from an integration.",
          "The order of trust is fixed: a queue the person chose; then a restricted queue the request is about, however sure the match; then the group’s routing rules; then the best-matching queue if the match clears the group’s auto-route threshold; then, for email, the classification ladder; otherwise the default queue, flagged for a person.",
          "Test the assistant (Configuration → Intake) shows where a message would go, how sure it was and why, before anything is real.",
          "After rewording a queue, an administrator can right-click a misplaced ticket and choose Re-run routing: it is read again and moved if it now belongs elsewhere, with a note saying why. A confidential ticket is never moved out this way.",
        ],
      },
      {
        id: "request-handling",
        title: "Security reports and other requests a person must see",
        summary: "Request types are read by meaning; routing rules can set priority, keep the assistant out, stop auto-close and alert people.",
        area: 'intake',
        keywords: ["security", "phishing", "vishing", "priority", "critical", "alert", "hold", "assistant", "auto-close", "request type"],
        body: [
          "A request type can say what belongs under it (Configuration → Request types, “What belongs here”). The same model that picks the queue then picks the type by meaning, for every new request: a report of a fake support call that asked someone to install remote software is a security report, not a software install. A type the person chose stands; a type with no description is left to the keyword ladder.",
          "A routing rule can also say how a ticket is handled: its priority; keep the assistant out, so it is acknowledged and left for a person rather than answered from an article; never close by itself, so neither auto-close nor a requester’s “done” closes it; and alert people by email when it arrives. For where a ticket goes the first matching rule decides, but every matching rule's handling counts, and the strictest wins.",
          "Each handled ticket gets a note naming the rules and what they did. Test the assistant shows the type it read and the handling before anything is real, and Re-run routing applies both to an existing ticket, reopening one the assistant had resolved.",
          "Which type counts as a security report for campaign detection is a group setting (Tenant settings → Security reports).",
          "A request type can have its own acknowledgement (Queues & types, edit the type). A new email ticket of that type gets it instead of the desk's, so a security report can carry its own instructions. {ticket} becomes the ticket number.",
          "A described request type also counts as something the desk handles: the off-topic check will not decline it, even if the Intake scope list leaves it out.",
          "Bulletins show at the top of every page. Post one with the megaphone in the top bar (queue leads and above), or from a confirmed security campaign (Dashboard, Security, Real, Post a bulletin), which prefills what was reported and what to do. Choose severity, who sees it and when it ends; a bulletin for everyone is also added to the desk's emails to requesters unless you switch that off. Take it down when it is over. A critical bulletin cannot be hidden by the reader.",
        ],
      },
      {
        id: "email-assistant",
        title: "What the email assistant does with a message",
        summary: "Screen it, route it, answer it from the knowledgebase or file it — and say which.",
        area: 'intake',
        keywords: ["email", "assistant", "answer", "decline", "partial", "acknowledge", "language", "screen"],
        body: [
          "Each email is screened first: machine mail is logged, personal data the group does not allow and messages outside what the desk handles are declined with a reply saying why, and so is strong language — unless the group has switched that filter off (an HR desk needs to see what was written).",
          "A new request the knowledgebase covers is answered and resolved. One that asks for several things is answered only where an article covers it: the reply says the rest is with the team, the ticket stays open, and its \"What they asked for\" list shows what is still to do. Anything else is filed and acknowledged with the ticket number.",
          "A reply to a ticket the assistant answered is read first: a thank-you leaves it resolved, \"close it\" closes it, anything else reopens it for a person. If the group allows it, a NEW question may get one more answer from a different article; \"it still doesn’t work\" never does.",
          "The group’s Mail tab lists every email read and what happened to it. The Intake tab holds the assistant’s settings, and Test the assistant shows exactly what a message would do with them.",
        ],
      },

      {
        id: 'needs-routing',
        title: 'Needs routing',
        summary: 'The router refused to guess and is asking a person.',
        area: 'intake',
        keywords: ['unrouted', 'triage', 'confidence', 'unclassified'],
        body: [
          'When nothing claims a request with enough confidence — no rule, and no queue description matched above the group’s threshold — it lands in the default queue flagged instead of guessed. Those show under the "Needs routing" chip on the queue.',
          'Picking a queue or taking the ticket clears the flag. That correction is recorded, which is what makes the routing rules improvable rather than a matter of opinion.',
        ],
      },
      {
        id: 'suppressed',
        title: 'Mail that was deliberately not ticketed',
        summary: 'Machine mail is logged, not turned into work.',
        area: 'intake',
        keywords: ['noise', 'suppressed', 'automated', 'notification', 'spam'],
        body: [
          'Automated notifications that need no human action are recorded in the suppressed log rather than becoming tickets. The log says which rule caught each one, so a rule that is too greedy is visible rather than silently eating real requests.',
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'knowledge',
    title: 'Knowledge',
    blurb: 'Articles the desk and the assistant answer from.',
    area: 'knowledge',
    topics: [
      {
        id: "kb-lifecycle",
        title: "From draft to published",
        summary: "Drafted, reviewed, approved, published — never published by itself.",
        area: 'knowledge',
        keywords: ["article", "draft", "review", "publish", "withdraw", "kb"],
        body: [
          "Articles start as drafts — written by a person, or drafted from problems the desk keeps solving. A reviewer approves or rejects each version, and only a publisher puts it in the index the assistant and the Known solutions panel read from.",
          "Agent-facing and requester-facing articles are kept apart. Editing a published article leaves the live version answering until the new one is approved; withdrawing takes it out of the index without losing it.",
        ],
      },
      {
        id: "kb-delete",
        title: "Deleting an article",
        summary: "Deleted articles are hidden, kept whole, and can be restored.",
        area: 'knowledge',
        keywords: ["delete", "remove", "restore", "article"],
        body: [
          "Delete is in the article window and asks why. A published article has to be withdrawn from the index first, so nothing still answering requesters goes missing. Authors can delete their own drafts; publishers can delete any article.",
          "A deleted article disappears from Knowledge and from every check that looks for one, but nothing is lost: a publisher can restore it, exactly as it was, from Deleted articles at the bottom of the page.",
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'reporting',
    title: 'Reporting',
    blurb: 'What the numbers mean, and what they deliberately do not say.',
    area: 'dashboard',
    topics: [
      {
        id: 'median-not-mean',
        title: 'Why there is no average anywhere',
        summary: 'Median and p90 throughout. The mean is not offered.',
        area: 'dashboard',
        path: '/dashboard',
        keywords: ['average', 'mean', 'median', 'p90', 'percentile', 'tail'],
        body: [
          'Resolution times have a long tail, and a mean over a long tail describes nothing that happened. The median says what a normal ticket looks like; the p90 says what a bad week looks like.',
          'The tail ratio — p90 divided by median — is the single number worth watching. When it is high, the problem is the tail, not the typical case, and speeding up the typical case will not move it.',
        ],
      },
      {
        id: 'tabs',
        title: 'The report tabs',
        summary: 'Overview, Workload, Backlog, Demand, Quality, Recurrence.',
        area: 'dashboard',
        path: '/dashboard',
        keywords: ['workload', 'backlog', 'demand', 'quality', 'recurrence', 'reports'],
        body: [
          'Overview is the state of the service. Workload is who is carrying what, including a balance figure for the whole team. Backlog is the open work by age rather than as one number.',
          'Demand is when work arrives and from where — the day-and-hour grid is the staffing evidence. Quality is the whole distribution and its trend. Recurrence is the same person asking the same thing again, which is the case for fixing something rather than answering faster.',
          'Every tab exports to CSV, and the export is generated from the same query the screen uses rather than from whatever the screen happened to load.',
        ],
      },
      {
        id: 'partial-month',
        title: 'Why the current month looks empty',
        summary: 'It is marked "so far" and left out of comparisons.',
        area: 'dashboard',
        keywords: ['month', 'partial', 'trend', 'incomplete'],
        body: [
          'The month in progress is a partial bucket. It is labelled as such and excluded from period comparisons, because reading it as a complete month turns every month-end into an apparent collapse in demand and can report a headline like "100% attainment" off three tickets.',
        ],
      },
      {
        id: 'cross-tenant',
        title: 'Comparing groups',
        summary: 'Firm-wide comparison needs platform admin.',
        area: 'comparison',
        minRole: 'platform_admin',
        path: '/comparison',
        keywords: ['comparison', 'firm', 'cross-tenant', 'all groups'],
        body: [
          'The comparison report is the only view that reads across every group, and it is restricted to platform admin and audited on every call.',
          'It exists because nothing in a per-group number showed the 6.6x gap between the two admin teams. A figure that only makes sense next to another group’s figure has to be somewhere.',
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'config',
    title: 'Configuration',
    blurb: 'What a group admin can change without a deployment.',
    area: 'admin',
    minRole: 'tenant_admin',
    topics: [
      {
        id: 'what-is-editable',
        title: 'What you can change',
        summary: 'Fields, routing, SLA, business hours, queues, request types, intake, mail and access.',
        area: 'admin',
        minRole: 'tenant_admin',
        path: '/admin',
        keywords: ['config', 'settings', 'edit', 'admin', 'queues', 'fields'],
        body: [
          'All of it is data, and none of it needs a release. Every change is versioned and attributed, and the History tab shows who changed what and when.',
          'Changes apply immediately — the tenant configuration cache is cleared on every write, so you will not see a stale value for the next fifteen seconds.',
          'A Request manager — a grant held alongside an agent role — can change the request settings (fields, routing, SLA, queues and types) and grant access up to their own level, without the rest of administration.',
        ],
      },
      {
        id: 'field-consumer',
        title: 'Why a new field demands a consumer',
        summary: 'You must name the report, rule or automation that will read it.',
        area: 'admin',
        minRole: 'tenant_admin',
        keywords: ['field', 'consumer', 'custom', 'required'],
        body: [
          'A field that nothing reads is a tax on every person who fills it in. The form will not create one without a stated consumer.',
          'This is the rule that stops the next unused sub-category from being invented. The record already has one: in a six-month sample, 364 tickets were filed under a sub-category about disabling the VPN, and only 11% of them mention the VPN.',
        ],
      },
      {
        id: 'versioned-fields',
        title: 'Editing a field creates a version',
        summary: 'Historic tickets keep the definition they were created under.',
        area: 'admin',
        minRole: 'tenant_admin',
        keywords: ['version', 'history', 'field', 'options'],
        body: [
          'Field definitions are versioned rather than edited in place. Renaming an option therefore cannot silently rewrite last year’s reporting — old tickets still carry the value they were given.',
        ],
      },
      {
        id: 'retire-not-delete',
        title: 'Retired, revoked, or deleted and restorable',
        summary: 'Fields, types, queues and rules retire; grants are revoked; tickets and articles delete softly.',
        area: 'admin',
        minRole: 'tenant_admin',
        keywords: ['delete', 'remove', 'retire', 'deactivate', 'close'],
        body: [
          'Retiring stops something being offered on new work while leaving every existing ticket readable. A routing rule that misrouted three months of tickets is the explanation for those tickets, and deleting it destroys the reason the data looks the way it does.',
          'A queue holding open tickets refuses to close and tells you how many are in the way — including confidential ones you cannot see. The default queue cannot be closed at all, because routing has to have somewhere to land.',
          'Tickets and knowledge articles can be deleted, but a delete hides rather than destroys: each has a Deleted list where it can be restored exactly as it was.',
        ],
      },
      {
        id: 'dry-run',
        title: 'Testing a routing change before it goes live',
        summary: 'Replay a candidate rule set against historic tickets.',
        area: 'admin',
        minRole: 'tenant_admin',
        keywords: ['dry run', 'replay', 'routing', 'test', 'simulate'],
        body: [
          'A candidate rule set can be run against tickets that already exist, so a routing change is judged on what it would have done rather than on what it was meant to do.',
        ],
      },
      {
        id: 'access',
        title: 'Granting and revoking access',
        summary: 'Roles are stored data with an explicit revocation.',
        area: 'admin',
        minRole: 'tenant_admin',
        keywords: ['role', 'permission', 'grant', 'revoke', 'access'],
        body: [
          'Roles run requester, agent, queue lead, group admin, platform admin — each includes everything below it. A group admin cannot create a platform admin; the API refuses it regardless of what any form offers.',
          'Revoking keeps the row with a timestamp rather than removing it, so the history of who could do what still reads correctly. Changing someone’s role is one step — pick the new role from the dropdown — and the history still shows what they held before.',
          'Two grants sit alongside a role rather than replacing it: Knowledge reviewer (approve and publish articles) and Request manager (request settings, and granting access up to their own level). Both can be given in the Grant access window.',
        ],
      },
      {
        id: "restricted-queues",
        title: "Private queues and their members",
        summary: "Restrict a queue so only its members see its tickets.",
        area: 'admin',
        minRole: 'tenant_admin',
        path: "/admin",
        keywords: ["restricted", "private", "confidential", "members", "hr", "queue"],
        body: [
          "Edit a queue and turn on Restricted, and describe what it is for — that description is what routes matching requests there, from email and the portal, before anyone else sees them. Then add members with the people button next to the queue.",
          "Members see the queue’s tickets; nobody else does, administrators included, unless they add themselves, and the member list marks anyone who did. The default queue cannot be restricted, because routing needs somewhere everyone can see.",
        ],
      },
      {
        id: "system-owners",
        title: "Who approves changes to other systems",
        summary: "Name the owners of Egnyte on Configuration → Actions.",
        area: 'admin',
        minRole: 'platform_admin',
        path: "/admin",
        keywords: ["owner", "egnyte", "approve", "action", "system"],
        body: [
          "A proposed action against a system waits for one of that system’s owners to approve it, and a system with no owner can have nothing approved. Owners are set per system for the whole platform, under System owners on the Actions page.",
          "With one owner, they may approve their own proposals (recorded as such); with two or more, somebody else has to.",
        ],
      },

    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'limits',
    title: 'What this does not do',
    blurb: 'Deliberate gaps, so you do not go looking for them.',
    area: 'personal',
    topics: [
      {
        id: 'not-built',
        title: 'Not built yet',
        summary: 'Canned responses, splitting a ticket, scheduled reports.',
        area: 'personal',
        keywords: ['missing', 'roadmap', 'later', 'phase'],
        body: [
          'There are no canned responses. Merging tickets works, splitting one does not. (Two agents on the same ticket do see each other named on it.)',
          'Reports export on demand but are not emailed on a schedule.',
        ],
      },
      {
        id: 'pending-gap',
        title: 'Imported tickets have no blocked reason',
        summary: 'FreshService recorded one "Pending" with no reason and no party.',
        area: 'personal',
        keywords: ['pending', 'blocked', 'imported', 'migration', 'waiting'],
        body: [
          'Tickets brought over from FreshService arrive with an empty waiting reason, because the source has nowhere to record one. That is not a loading fault — it is the gap this system splits into three separate waiting states to close.',
          'Anything raised here records what is being waited on, which is what makes the blocked-time report possible.',
        ],
      },
    ],
  },
];

/** Sections and topics the given role can actually act on. */
export function helpFor(role: string | null | undefined): HelpSection[] {
  return SECTIONS.map((section) => ({
    ...section,
    topics: section.topics.filter(
      (t) => canAccess(role, t.area) && (!t.minRole || hasRole(role, t.minRole)),
    ),
  })).filter(
    (section) =>
      section.topics.length > 0 &&
      canAccess(role, section.area) &&
      (!section.minRole || hasRole(role, section.minRole)),
  );
}

/** Free-text search across the topics the role can see. */
export function searchHelp(role: string | null | undefined, query: string): HelpTopic[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/);
  return helpFor(role)
    .flatMap((s) => s.topics)
    .map((t) => {
      const haystack = [t.title, t.summary, ...(t.keywords ?? []), ...t.body]
        .join(' ')
        .toLowerCase();
      const score = terms.reduce((acc, term) => {
        if (!haystack.includes(term)) return acc - 100;
        return acc + (t.title.toLowerCase().includes(term) ? 3 : 1);
      }, 0);
      return { t, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.t);
}

/** The topics most relevant to the page the user is on. */
export function helpForPath(role: string | null | undefined, path: string): HelpTopic[] {
  const all = helpFor(role).flatMap((s) => s.topics);
  const exact = all.filter((t) => t.path && path.startsWith(t.path));
  if (exact.length > 0) return exact;
  if (path.startsWith('/t/')) return all.filter((t) => t.area === 'queue');
  return [];
}

/** Total topics visible to a role — used to say "12 topics for your role". */
export function helpTopicCount(role: string | null | undefined): number {
  return helpFor(role).reduce((a, s) => a + s.topics.length, 0);
}
